<template>
    <div class="page categories-page">
        <Modal
            v-model:open="open"
            :title="categoriesForm.id ? '編輯分類' : '新增分類'"
            :width="600"
            @ok="handleEdit"
            @cancel="handleFormReset"
        >
            <a-form
                ref="form"
                :model="categoriesForm"
                :rules="formRules"
                layout="vertical"
                class="category-form"
            >
                <a-form-item
                    v-if="!categoriesForm.id"
                    label="分類類型"
                    name="category_type"
                    class="form-item-full"
                >
                    <a-radio-group
                        v-model:value="categoryType"
                        name="categoryType"
                        size="large"
                        class="type-radio-group"
                        @change="handleCategoryTypeChange"
                    >
                        <a-radio value="parent" class="radio-option">
                            大分類
                        </a-radio>
                        <a-radio value="child" class="radio-option">
                            子分類
                        </a-radio>
                    </a-radio-group>
                </a-form-item>

                <a-form-item
                    v-if="categoryType === 'child' && !categoriesForm.id"
                    label="所屬大分類"
                    name="parent_id"
                    class="form-item-full"
                >
                    <a-select
                        v-model:value="categoriesForm.parent_id"
                        placeholder="請選擇大分類"
                        size="large"
                        class="parent-select"
                        :options="parentCategories"
                        allow-clear
                    ></a-select>
                </a-form-item>

                <a-form-item
                    v-if="categoriesForm.id && categoriesForm.parent_id"
                    label="所屬大分類"
                    class="form-item-full"
                >
                    <a-input
                        :value="getParentCategoryName(categoriesForm.parent_id)"
                        disabled
                        size="large"
                        class="vaporwave-input-field"
                    />
                </a-form-item>

                <a-form-item
                    label="分類名稱"
                    name="name"
                    class="form-item-full"
                >
                    <a-input
                        v-model:value="categoriesForm.name"
                        placeholder="請輸入分類名稱"
                        size="large"
                        class="vaporwave-input-field"
                    />
                </a-form-item>

                <a-form-item
                    label="是否啟用此分類"
                    name="is_active"
                    class="form-item-full"
                >
                    <a-radio-group
                        v-model:value="categoriesForm.is_active"
                        name="radioGroup"
                        size="large"
                        class="status-radio-group"
                    >
                        <a-radio value="false" class="radio-option">
                            停用
                        </a-radio>
                        <a-radio value="true" class="radio-option">
                            啟用
                        </a-radio>
                    </a-radio-group>
                </a-form-item>
            </a-form>
        </Modal>

        <div class="page-header">
            <h1 class="page-title">商品分類</h1>
            <p class="page-subtitle">查看並管理所有的商品分類</p>
        </div>

        <div class="content vaporwave-theme">
            <!-- 工具欄 -->
            <div class="toolbar-section">
                <div class="filters-group">
                    <a-space wrap size="large" class="filter-space">
                        <div class="filter-item">
                            <span class="filter-label">啟用狀態：</span>
                            <a-select
                                ref="active_select"
                                v-model:value="activeValue"
                                size="middle"
                                class="status-select min-w-[160px]"
                                :options="activeOpt"
                                allow-clear
                                placeholder="全部"
                            ></a-select>
                        </div>

                        <div class="search-wrapper">
                            <a-input
                                v-model:value="searchText"
                                placeholder="搜尋商品分類名稱"
                                size="middle"
                                class="search-input-field"
                                allow-clear
                                @pressEnter="handleSearch"
                            />
                        </div>
                    </a-space>
                </div>

                <div class="actions-group">
                    <a-space size="middle" wrap>
                        <a-button
                            v-if="canAdd"
                            type="primary"
                            size="middle"
                            class="unified-add-btn"
                            @click="handleAddCategory"
                        >
                            新增分類
                        </a-button>
                        <a-button
                            size="middle"
                            class="unified-search-btn"
                            @click="handleSearch"
                        >
                            查詢
                        </a-button>
                        <a-button
                            size="middle"
                            class="unified-reset-btn"
                            @click="handleReset"
                        >
                            重置
                        </a-button>
                    </a-space>
                </div>
            </div>

            <!-- 商品分類列表表格 -->
            <div class="table-wrapper">
                <DataTable
                    :showIndex="false"
                    title=""
                    :columns="columns"
                    :data-source="productCategories"
                    :pagination="pagination"
                    :loading="loading"
                    :expandable="{
                        defaultExpandAllRows: false,
                        childrenColumnName: 'children',
                        expandIcon: ({ expanded, onExpand, record }) => {
                            if (record.children && record.children.length > 0) {
                                return h(
                                    expanded ? DownOutlined : RightOutlined,
                                    {
                                        onClick: (e) => onExpand(record, e)
                                    }
                                );
                            }
                            return null;
                        }
                    }"
                    @refresh="loadCategories"
                    @change="handleTableChange"
                    class="categories-table"
                >
                    <template #bodyCell="{ column, record }">
                        <template v-if="column.key === 'name'">
                            <div
                                class="category-name"
                                :class="{
                                    'is-parent': !record.parent_id,
                                    'is-child': record.parent_id
                                }"
                            >
                                <span class="name-text">{{ record.name }}</span>
                            </div>
                        </template>

                        <template v-else-if="column.key === 'sort'">
                            <SortStepper
                                v-if="canEdit"
                                :loading="movingId === record.id"
                                @up="moveCategory(record, 'up')"
                                @down="moveCategory(record, 'down')"
                            />
                            <span v-else>{{ record.sort }}</span>
                        </template>

                        <template v-else-if="column.key === 'is_active'">
                            <a-tag
                                :color="record.is_active ? 'green' : 'red'"
                                class="status-tag"
                            >
                                {{ record.is_active ? '啟用' : '停用' }}
                            </a-tag>
                        </template>

                        <template v-else-if="column.key === 'products_count'">
                            <span class="count-badge">
                                {{ getTotalProductsCount(record) }}
                            </span>
                        </template>

                        <template v-else-if="column.key === 'actions'">
                            <a-space class="action-buttons" size="small" wrap>
                                <a-button
                                    v-if="canEdit"
                                    size="small"
                                    class="action-btn-edit"
                                    @click="goEdit(record)"
                                >
                                    修改
                                </a-button>
                                <a-tooltip
                                    v-if="
                                        canDelete && !canDeleteCategory(record)
                                    "
                                    :title="getDeleteDisabledReason(record)"
                                >
                                    <a-button
                                        size="small"
                                        class="action-btn-delete"
                                        danger
                                        disabled
                                    >
                                        刪除
                                    </a-button>
                                </a-tooltip>
                                <a-button
                                    v-else-if="canDelete"
                                    size="small"
                                    class="action-btn-delete"
                                    danger
                                    @click="confirmDelete(record)"
                                >
                                    刪除
                                </a-button>
                                <a-button
                                    v-if="canAdd && !record.parent_id"
                                    size="small"
                                    class="action-btn-add-child"
                                    @click="goAddChildCategory(record.id)"
                                >
                                    新增子分類
                                </a-button>
                            </a-space>
                        </template>
                    </template>
                </DataTable>
            </div>
        </div>

        <!-- 刪除分類 Modal -->
        <a-modal
            v-model:open="deleteModalVisible"
            title="刪除分類"
            :width="600"
            :confirm-loading="deleteLoading"
            @ok="handleConfirmDelete"
            @cancel="deleteModalVisible = false"
        >
            <div v-if="deleteTarget" class="delete-modal-content">
                <!-- 分類資訊 -->
                <div class="delete-category-info">
                    <h3 class="delete-section-title">分類資訊</h3>
                    <a-descriptions :column="2" bordered size="small">
                        <a-descriptions-item label="分類名稱">
                            <span class="category-name-display">
                                {{ deleteTarget.name }}
                            </span>
                        </a-descriptions-item>
                        <a-descriptions-item label="分類類型">
                            <a-tag
                                :color="
                                    deleteTarget.parent_id ? 'blue' : 'purple'
                                "
                            >
                                {{
                                    deleteTarget.parent_id ? '子分類' : '大分類'
                                }}
                            </a-tag>
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="deleteTarget.parent_id"
                            label="所屬大分類"
                        >
                            {{ getParentCategoryName(deleteTarget.parent_id) }}
                        </a-descriptions-item>
                        <a-descriptions-item label="啟用狀態">
                            <a-tag
                                :color="
                                    deleteTarget.is_active ? 'green' : 'red'
                                "
                            >
                                {{ deleteTarget.is_active ? '啟用' : '停用' }}
                            </a-tag>
                        </a-descriptions-item>
                        <a-descriptions-item label="商品數" :span="2">
                            <span class="products-count">
                                {{ getTotalProductsCount(deleteTarget) }}
                            </span>
                        </a-descriptions-item>
                    </a-descriptions>
                </div>

                <!-- 操作提示 -->
                <div class="delete-action-hint">
                    <a-alert
                        type="warning"
                        message="此操作無法復原，請確認後再執行"
                        show-icon
                    />
                </div>
            </div>
            <template #footer>
                <a-button @click="deleteModalVisible = false">取消</a-button>
                <a-button
                    type="primary"
                    danger
                    :loading="deleteLoading"
                    @click="handleConfirmDelete"
                >
                    確認刪除
                </a-button>
            </template>
        </a-modal>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, h } from 'vue';
import { message, Modal as AntModal } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';
import {
    RightOutlined,
    DownOutlined,
    SearchOutlined,
    ReloadOutlined
} from '@ant-design/icons-vue';
import DataTable from '@/components/common/DataTable.vue';
import Modal from '@/components/common/Modal.vue';
import SortStepper from '@/components/common/SortStepper.vue';
import {
    createShopCategories,
    getShopCategories,
    updateShopCategories,
    deleteShopCategories,
    moveShopCategory
} from '@/api/products';
import { useRouter } from 'vue-router';
import { useAdminInfo } from '@/stores/useAdminInfo';

const router = useRouter();
const adminStore = useAdminInfo();
const form = ref(null);
const loading = ref(false);
const searchText = ref('');
const productCategories = ref([]);
const activeValue = ref(null);
const open = ref(false);
const categoryType = ref('parent');

// 刪除 Modal 狀態
const deleteModalVisible = ref(false);
const deleteTarget = ref(null);
const deleteLoading = ref(false);
const movingId = ref(null);
const categoriesForm = ref({
    id: null,
    name: null,
    parent_id: null,
    sort: 0,
    is_active: 'false'
});

// 分頁設定
const pagination = reactive({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `共 ${total} 筆`
});

// 表格欄位
const columns = computed(() => {
    const baseColumns = [
        {
            title: '名稱',
            key: 'name',
            dataIndex: 'name',
            width: 150,
            align: 'center'
        },
        {
            title: '排序',
            key: 'sort',
            dataIndex: 'sort',
            width: 72,
            align: 'center'
        },
        {
            title: '啟用',
            key: 'is_active',
            dataIndex: 'is_active',
            width: 120,
            align: 'center'
        },
        {
            title: '商品數',
            key: 'products_count',
            dataIndex: 'products_count',
            width: 120,
            align: 'center'
        }
    ];

    // 如果有新增、修改或刪除任一權限，才顯示操作欄位
    if (canAdd.value || canEdit.value || canDelete.value) {
        baseColumns.push({
            title: '操作',
            key: 'actions',
            align: 'center',
            width: 180,
            fixed: 'right'
        });
    }

    return baseColumns;
});

const activeOpt = [
    { label: '全部', value: null },
    { label: '啟用', value: 1 },
    { label: '停用', value: 0 }
];

// 權限檢查
const canView = computed(() => adminStore.hasPermissionCode('150104')); // 查看分類
const canAdd = computed(() => adminStore.hasPermissionCode('150101')); // 新增分類
const canEdit = computed(() => adminStore.hasPermissionCode('150102')); // 修改分類
const canDelete = computed(() => adminStore.hasPermissionCode('150103')); // 刪除分類

// 計算父分類選項（只顯示大分類）
const parentCategories = computed(() => {
    return productCategories.value
        .filter((cat) => !cat.parent_id)
        .map((cat) => ({
            label: cat.name,
            value: cat.id
        }));
});

// 根據 parent_id 獲取父分類名稱
const getParentCategoryName = (parentId) => {
    const parent = productCategories.value.find((cat) => cat.id === parentId);
    return parent ? parent.name : '';
};

// 計算分類的總商品數（包含所有子分類）
const getTotalProductsCount = (record) => {
    // 如果是子分類，直接返回自己的數量
    if (record.parent_id) {
        return record.products_count || 0;
    }

    // 如果是大分類，計算自己 + 所有子分類的數量
    let total = record.products_count || 0;

    // 遞迴計算所有子分類的商品數
    if (record.children && Array.isArray(record.children)) {
        record.children.forEach((child) => {
            total += child.products_count || 0;
        });
    }

    return total;
};

// 判斷是否可以刪除分類
const canDeleteCategory = (record) => {
    // 如果是大分類，檢查是否有子分類
    if (!record.parent_id) {
        const childrenCount = productCategories.value.filter(
            (cat) => cat.parent_id === record.id
        ).length;
        if (childrenCount > 0) return false;
    }
    // 檢查是否有商品（包含子分類的商品數）
    return getTotalProductsCount(record) === 0;
};

// 取得刪除禁用原因
const getDeleteDisabledReason = (record) => {
    if (!canDeleteCategory(record)) {
        // 如果是大分類，檢查是否有子分類
        if (!record.parent_id) {
            const childrenCount = productCategories.value.filter(
                (cat) => cat.parent_id === record.id
            ).length;
            if (childrenCount > 0) {
                return '此分類下有子分類，無法刪除';
            }
        }
        // 檢查是否有商品（包含子分類的商品數）
        const totalCount = getTotalProductsCount(record);
        if (totalCount > 0) {
            return '此分類下有商品，無法刪除';
        }
    }
    return '';
};

const formRules = {
    name: [{ required: true, message: '請輸入名稱' }]
};

// 處理分類數據，確保 children 陣列格式正確
const processCategoryData = (categories) => {
    if (!categories || categories.length === 0) return [];

    // API 已經回傳正確的 children 結構，只需要確保空陣列轉為 undefined
    return categories.map((cat) => {
        const processed = { ...cat };
        // 如果 children 是空陣列，轉為 undefined（Ant Design Table 需要）
        if (
            Array.isArray(processed.children) &&
            processed.children.length === 0
        ) {
            processed.children = undefined;
        }
        return processed;
    });
};

// 載入分類列表
const loadCategories = async () => {
    // 檢查查看權限
    if (!canView.value) {
        showErrorMessage('您沒有查看分類的權限');
        return;
    }

    try {
        loading.value = true;

        const params = {
            page: pagination.current,
            per_page: pagination.pageSize
        };

        if (searchText.value) {
            params['keyword'] = searchText.value;
        }
        if (activeValue.value !== null && activeValue.value !== undefined) {
            params['is_active'] = activeValue.value;
        }

        const response = await getShopCategories(params);

        if (response.result) {
            const rawData = response.data.data || [];
            // 處理數據，組織成樹狀結構
            productCategories.value = processCategoryData(rawData);
            pagination.total = response.data.pagination.total || 0;
            pagination.current = response.data.pagination.current_page || 1;
        } else {
            showErrorMessage(response.message || '載入失敗');
        }
    } catch (error) {
        showErrorMessage('載入商品分類失敗');
    } finally {
        loading.value = false;
    }
};

// 處理表格變化
const handleTableChange = (pag, filters, sorter) => {
    const { pagination: p } = pag;
    pagination.current = p.current;
    pagination.pageSize = p.pageSize;
    loadCategories();
};

// 搜尋
const handleSearch = () => {
    pagination.current = 1;
    loadCategories();
};

// 重置篩選條件
const handleReset = () => {
    activeValue.value = null;
    searchText.value = '';
    pagination.current = 1;
    pagination.pageSize = 10;
    loadCategories();
};

const handleFormReset = () => {
    categoriesForm.value.id = null;
    categoriesForm.value.name = null;
    categoriesForm.value.parent_id = null;
    categoriesForm.value.sort = 0;
    categoriesForm.value.is_active = 'false';
    categoryType.value = 'parent';
};

const handleCategoryTypeChange = () => {
    if (categoryType.value === 'parent') {
        categoriesForm.value.parent_id = null;
    }
};

// 刷新
const handleRefresh = () => {
    loadCategories();
};

const confirmDelete = (record) => {
    if (!canDelete.value) {
        message.warning('您沒有刪除分類的權限');
        return;
    }

    deleteTarget.value = record;
    deleteModalVisible.value = true;
};

const handleConfirmDelete = async () => {
    if (!deleteTarget.value) return;

    try {
        deleteLoading.value = true;
        const response = await deleteShopCategories(deleteTarget.value.id);

        if (response.result) {
            message.success('刪除成功');
            deleteModalVisible.value = false;
            deleteTarget.value = null;
            loadCategories();
        } else {
            showErrorMessage(response.message || '刪除失敗');
        }
    } catch (error) {
        const { response } = error;
        const { data } = response || {};
        const errorMessage = data?.message || error?.message || '刪除失敗';
        showErrorMessage(errorMessage);
        console.error('刪除商品分類失敗:', error);
    } finally {
        deleteLoading.value = false;
    }
};

const handleEdit = async () => {
    try {
        await form.value?.validate();
    } catch (error) {
        return;
    }

    const { id } = categoriesForm.value;
    const formData = {
        name: categoriesForm.value.name,
        parent_id: categoriesForm.value.parent_id,
        is_active: categoriesForm.value.is_active === 'true'
    };

    if (id) {
        // 檢查修改權限
        if (!canEdit.value) {
            showErrorMessage('您沒有修改分類的權限');
            return;
        }

        try {
            const response = await updateShopCategories(id, formData);

            if (response.result) {
                message.success('更新成功');
                open.value = false;
                loadCategories();
                handleFormReset();
            } else {
                showErrorMessage(response.message || '更新失敗');
            }
        } catch (error) {
            const { response } = error;
            const { data } = response || {};
            const errorMessage = data?.message || error?.message || '更新失敗';
            showErrorMessage(errorMessage);
            console.error('更新商品分類失敗:', error);
        }
    } else {
        // 檢查新增權限
        if (!canAdd.value) {
            showErrorMessage('您沒有新增分類的權限');
            return;
        }

        try {
            const response = await createShopCategories(formData);

            if (response.result) {
                message.success('建立成功');
                open.value = false;
                loadCategories();
                handleFormReset();
            } else {
                showErrorMessage(response.message || '建立失敗');
            }
        } catch (error) {
            const { response } = error;
            const { data } = response || {};
            const errorMessage = data?.message || error?.message || '建立失敗';
            showErrorMessage(errorMessage);
            console.error('建立商品分類失敗:', error);
        }
    }
};

const goEdit = (record) => {
    // 檢查修改權限
    if (!canEdit.value) {
        showErrorMessage('您沒有修改分類的權限');
        return;
    }

    categoriesForm.value = {
        id: record.id,
        name: record.name,
        parent_id: record.parent_id,
        sort: record.sort || 0,
        is_active: record.is_active ? 'true' : 'false'
    };
    open.value = true;
};

const handleAddCategory = () => {
    // 檢查新增權限
    if (!canAdd.value) {
        showErrorMessage('您沒有新增分類的權限');
        return;
    }

    handleFormReset();
    categoryType.value = 'parent';
    open.value = true;
};

const moveCategory = async (record, direction) => {
    if (!canEdit.value) {
        showErrorMessage('您沒有修改分類的權限');
        return;
    }

    try {
        movingId.value = record.id;
        const response = await moveShopCategory(record.id, direction);
        if (response.result) {
            message.success('排序已更新');
            await loadCategories();
        } else {
            showErrorMessage(response.message || '排序更新失敗');
        }
    } catch (error) {
        const { response } = error;
        const { data } = response || {};
        showErrorMessage(data?.message || error?.message || '排序更新失敗');
    } finally {
        movingId.value = null;
    }
};

const goAddChildCategory = (parent_id) => {
    // 檢查新增權限
    if (!canAdd.value) {
        showErrorMessage('您沒有新增分類的權限');
        return;
    }

    handleFormReset();
    categoryType.value = 'child';
    categoriesForm.value.parent_id = parent_id;
    open.value = true;
};

onMounted(() => {
    loadCategories();
});
</script>

<style scoped>
/* 頁面容器 */
.categories-page {
    padding: 32px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.98),
        rgba(248, 249, 250, 0.95)
    );
    min-height: 100vh;
    animation: pageFadeIn 0.5s ease-out;
}

@keyframes pageFadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* 頁面標題 */
.page-header {
    margin-bottom: 32px;
    position: relative;
}

.page-title {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 12px 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.page-subtitle {
    margin: 0;
    color: #6a6a7a;
    font-size: 14px;
    font-family: 'Courier New', monospace;
    font-weight: 400;
}

/* 內容容器 */
.content {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 16px;
    padding: 32px;
    min-height: 500px;
    position: relative;
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.3);
    transition: all 0.3s ease;
}

.content::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
    opacity: 0.8;
    transition: opacity 0.3s ease;
}

/* 工具欄區域 */
.toolbar-section {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-bottom: 32px;
    position: relative;
    z-index: 1;
}

.filters-group {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
}

.filter-space {
    width: 100%;
}

.filter-item {
    display: flex;
    align-items: center;
    gap: 12px;
}

.filter-label {
    font-family: 'Courier New', monospace;
    font-weight: 600;
    color: #2a2a3a;
    font-size: 14px;
    white-space: nowrap;
}

.status-select {
    width: 140px;
}

.search-wrapper {
    flex: 1;
    min-width: 240px;
    max-width: 320px;
}

/* 搜尋框樣式見 @/styles/page-layout.css（search-input-field） */

.actions-group {
    display: flex;
    justify-content: flex-end;
    align-items: center;
}

/* 表格容器 */
.table-wrapper {
    position: relative;
    z-index: 1;
}

/* 分類名稱樣式 */
.category-name {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex-wrap: wrap;
    position: relative;
}

.category-name.is-parent {
    font-weight: 600;
}

.category-name.is-child {
    font-weight: 400;
    padding-left: 24px;
}

/* 移除縮排，因為使用 expandable table */

.name-text {
    font-family: 'Courier New', monospace;
    color: #2a2a3a;
    font-size: 14px;
    text-align: center;
}

.category-name.is-parent .name-text {
    color: #2a2a3a;
    font-weight: 600;
}

.category-name.is-child .name-text {
    color: rgba(42, 42, 58, 0.75);
    font-weight: 400;
}

/* 狀態標籤 */
.status-tag {
    font-family: 'Courier New', monospace;
    font-weight: 500;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
}

/* 數量徽章 */
.count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 24px;
    padding: 0 10px;
    background: linear-gradient(
        135deg,
        rgba(42, 74, 122, 0.15),
        rgba(138, 175, 255, 0.15)
    );
    border: 1px solid rgba(42, 74, 122, 0.3);
    border-radius: 12px;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-weight: 600;
    font-size: 13px;
}

/* 主要操作按鈕 */
.primary-action-btn {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5) !important;
    border: none !important;
    color: #ffffff !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    border-radius: 8px !important;
    padding: 6px 16px !important;
    height: 32px !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.35) !important;
    transition: all 0.3s ease !important;
    display: inline-flex !important;
    align-items: center !important;
    gap: 6px !important;
}

.primary-action-btn:hover {
    background: linear-gradient(45deg, #3b6ea5, #2a4a7a) !important;
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.6) !important;
    transform: translateY(-2px) !important;
}

.primary-action-btn :deep(.anticon) {
    color: #ffffff !important;
}

/* 次要操作按鈕 */
.secondary-action-btn {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    font-size: 13px !important;
    border-radius: 8px !important;
    padding: 6px 16px !important;
    height: 32px !important;
    transition: all 0.3s ease !important;
    display: inline-flex !important;
    align-items: center !important;
    gap: 6px !important;
}

.secondary-action-btn:hover {
    background: rgba(42, 74, 122, 0.1) !important;
    border-color: rgba(42, 74, 122, 0.6) !important;
    color: #2a4a7a !important;
    transform: translateY(-2px) !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2) !important;
}

.secondary-action-btn :deep(.anticon) {
    color: inherit !important;
}

/* 表單樣式 */
.category-form :deep(.ant-form-item-label > label) {
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    color: #2a2a3a !important;
    font-size: 14px !important;
}

.vaporwave-input-field :deep(.ant-input) {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    padding: 10px 16px !important;
    transition: all 0.3s ease !important;
}

.vaporwave-input-field :deep(.ant-input:hover) {
    border-color: rgba(42, 74, 122, 0.5) !important;
    box-shadow: 0 0 10px rgba(42, 74, 122, 0.2) !important;
}

.vaporwave-input-field :deep(.ant-input:focus) {
    border-color: #2a4a7a !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.3) !important;
}

.status-radio-group :deep(.ant-radio-wrapper) {
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    color: #2a2a3a !important;
    margin-right: 24px !important;
}

.status-radio-group :deep(.ant-radio-inner) {
    border-color: rgba(200, 200, 210, 0.4) !important;
}

.status-radio-group :deep(.ant-radio-checked .ant-radio-inner) {
    border-color: #2a4a7a !important;
    background-color: #2a4a7a !important;
}

.status-radio-group :deep(.ant-radio-checked .ant-radio-inner::after) {
    background-color: #ffffff !important;
}

/* 分類類型單選按鈕 */
.type-radio-group :deep(.ant-radio-wrapper) {
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    color: #2a2a3a !important;
    margin-right: 24px !important;
}

.type-radio-group :deep(.ant-radio-inner) {
    border-color: rgba(200, 200, 210, 0.4) !important;
}

.type-radio-group :deep(.ant-radio-checked .ant-radio-inner) {
    border-color: #2a4a7a !important;
    background-color: #2a4a7a !important;
}

.type-radio-group :deep(.ant-radio-checked .ant-radio-inner::after) {
    background-color: #ffffff !important;
}

/* 父分類選擇器 */
.parent-select :deep(.ant-select-selector) {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    height: 40px !important;
    transition: all 0.3s ease !important;
}

.parent-select :deep(.ant-select-selector:hover) {
    border-color: rgba(42, 74, 122, 0.5) !important;
}

.parent-select :deep(.ant-select-focused .ant-select-selector) {
    border-color: #2a4a7a !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.3) !important;
}

/* 選擇器樣式 */
.status-select :deep(.ant-select) {
    border-radius: 8px !important;
}

.status-select :deep(.ant-select-selector) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    height: 32px !important;
    transition: all 0.3s ease !important;
}

.status-select :deep(.ant-select-selector:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2) !important;
}

.status-select :deep(.ant-select-focused .ant-select-selector) {
    border-color: rgba(42, 74, 122, 0.8) !important;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.2) !important;
}

.status-select :deep(.ant-select-selection-item) {
    line-height: 30px !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

.status-select :deep(.ant-select-selection-placeholder) {
    line-height: 30px !important;
    font-size: 13px !important;
    color: rgba(42, 42, 58, 0.5) !important;
    font-family: 'Courier New', monospace !important;
}

.status-select :deep(.ant-select-arrow) {
    color: rgba(42, 42, 58, 0.6) !important;
    transition: color 0.3s ease !important;
}

.status-select :deep(.ant-select:hover .ant-select-arrow) {
    color: #2a4a7a !important;
}

/* 下拉選單樣式 */
.status-select :deep(.ant-select-dropdown) {
    background: rgba(255, 255, 255, 0.98) !important;
    backdrop-filter: blur(10px) !important;
    border: 1.5px solid rgba(42, 74, 122, 0.3) !important;
    border-radius: 12px !important;
    box-shadow: 0 0 25px rgba(42, 74, 122, 0.25) !important;
    padding: 8px !important;
    margin-top: 4px !important;
}

.status-select :deep(.ant-select-item) {
    background: transparent !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    border-radius: 8px !important;
    margin: 2px 0 !important;
    padding: 8px 12px !important;
    transition: all 0.2s ease !important;
}

.status-select :deep(.ant-select-item:hover) {
    background: rgba(42, 74, 122, 0.15) !important;
    color: #2a4a7a !important;
}

.status-select :deep(.ant-select-item-option-selected) {
    background: rgba(42, 74, 122, 0.25) !important;
    color: #2a4a7a !important;
    font-weight: 600 !important;
}

.status-select :deep(.ant-select-item-option-active) {
    background: rgba(42, 74, 122, 0.1) !important;
}

.status-select :deep(.ant-select-selector:hover) {
    border-color: rgba(42, 74, 122, 0.5) !important;
}

.status-select :deep(.ant-select-focused .ant-select-selector) {
    border-color: #2a4a7a !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.3) !important;
}

/* 展開按鈕樣式 */
.categories-table :deep(.ant-table-row-expand-icon) {
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    background: rgba(255, 255, 255, 0.95) !important;
    border-radius: 4px !important;
    transition: all 0.2s ease !important;
    width: 18px !important;
    height: 18px !important;
    min-width: 18px !important;
    min-height: 18px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    line-height: 1 !important;
    cursor: pointer !important;
    padding: 0 !important;
}

.categories-table :deep(.ant-table-row-expand-icon .anticon) {
    font-size: 11px !important;
    color: #2a2a3a !important;
    transition: all 0.2s ease !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
}

.categories-table :deep(.ant-table-row-expand-icon:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    background: rgba(42, 74, 122, 0.1) !important;
}

.categories-table :deep(.ant-table-row-expand-icon:hover .anticon) {
    color: #2a4a7a !important;
}

.categories-table :deep(.ant-table-row-expand-icon-expanded) {
    background: rgba(42, 74, 122, 0.1) !important;
    border-color: rgba(42, 74, 122, 0.6) !important;
}

.categories-table :deep(.ant-table-row-expand-icon-expanded .anticon) {
    color: #2a4a7a !important;
}

.categories-table :deep(.ant-table-row-expand-icon-expanded:hover) {
    background: rgba(42, 74, 122, 0.15) !important;
    border-color: rgba(42, 74, 122, 0.8) !important;
}

.categories-table :deep(.ant-table-row-expand-icon-expanded:hover .anticon) {
    color: #2a4a7a !important;
}

/* 響應式設計 */
@media (max-width: 768px) {
    .categories-page {
        padding: 16px;
    }

    .page-title {
        font-size: 24px;
    }

    .content-wrapper {
        padding: 20px;
    }

    .toolbar-section {
        flex-direction: column;
        gap: 16px;
    }

    .filters-group {
        width: 100%;
    }

    .actions-group {
        width: 100%;
        justify-content: stretch;
    }

    .actions-group :deep(.ant-space) {
        width: 100%;
    }

    .actions-group :deep(.ant-btn) {
        flex: 1;
    }

    .search-wrapper {
        width: 100%;
        min-width: 100%;
    }
}

/* 刪除分類 Modal 樣式 */
.delete-modal-content {
    font-family: 'Courier New', monospace;
}

.delete-category-info {
    margin-bottom: 24px;
}

.delete-section-title {
    font-size: 16px;
    font-weight: 700;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 2px solid rgba(138, 175, 255, 0.3);
}

.category-name-display {
    font-size: 15px;
    font-weight: 600;
    color: #2a2a3a;
}

.products-count {
    font-size: 16px;
    font-weight: 700;
    color: #2a4a7a;
}

.delete-action-hint {
    margin-top: 20px;
}

.delete-modal-content :deep(.ant-descriptions) {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(138, 175, 255, 0.2);
}

.delete-modal-content :deep(.ant-descriptions-item-label) {
    background: rgba(138, 175, 255, 0.1) !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    border-right: 1px solid rgba(138, 175, 255, 0.15) !important;
}

.delete-modal-content :deep(.ant-descriptions-item-content) {
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    background: rgba(255, 255, 255, 0.6) !important;
}
</style>
